import { ReactElement, forwardRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../button/button';
import { BUTTON_VARIANTS } from '../../../../constants/button-variants';
import cn from '../../../../utils/classnames';
import toKebabCase from '../../../../utils/to-kebab-case';
import { useOverlayTriggerContext } from '../../../overlay-trigger/overlay-trigger-context';
import Tooltip from '../../base/tooltip';
import { RichTooltipAction, RichTooltipProps } from './rich-tooltip.types';
import './rich-tooltip.scss';

const RichTooltip = forwardRef<HTMLDivElement, RichTooltipProps>((props, ref): ReactElement => {
    const { children, title, actions, isInteractable, className, id, ...rest } = props;

    const overlayTrigger = useOverlayTriggerContext();
    const hasActions = Boolean(actions?.length);

    useLayoutEffect(() => {
        const shouldStayOpenBecauseIsInteractable = hasActions || Boolean(isInteractable);

        overlayTrigger?.setInteractable(shouldStayOpenBecauseIsInteractable);

        return () => overlayTrigger?.setInteractable(false);
    }, [hasActions, isInteractable]);

    if (!children) {
        return <></>;
    }

    const classNames = cn('rich-tooltip', className);

    return (
        <Tooltip ref={ref} id={id} className={classNames} {...rest}>
            {title && <div className='tooltip-title'>{title}</div>}

            {children}

            {hasActions && (
                <div className='tooltip-actions'>
                    {(actions as RichTooltipAction[]).map((action, index) => {
                        const testId = toKebabCase(`${id}-action-${index + 1}`);

                        const actionProps = { key: action.title, className: 'tooltip-action', 'data-testid': testId };
                        const actionLabel = <span className='tooltip-action-title'>{action.title}</span>;

                        if (action.href) {
                            return (
                                <Link {...actionProps} to={action.href} target={action.target}>
                                    {actionLabel}
                                </Link>
                            );
                        }

                        return (
                            <Button {...actionProps} onClick={action.onClick} variant={BUTTON_VARIANTS.PLAIN_LINK}>
                                {actionLabel}
                            </Button>
                        );
                    })}
                </div>
            )}
        </Tooltip>
    );
});

RichTooltip.displayName = 'RichTooltip';

export default RichTooltip;
